/**
 * Site-wide scroll reveal — every band fades up a little as it scrolls into view.
 *
 * Applied automatically, no markup needed: `.block` is the shared root class of all
 * ACF blocks, and the short list below adds the handful of bands that are template
 * parts rather than blocks. A section can opt in explicitly with `data-reveal`, or
 * out with `.no-reveal` / `data-reveal="off"`.
 *
 * Two things are deliberately left alone:
 *
 * 1. Anything already on screen when the page loads. Nothing "scrolls into view"
 *    there, and it also means the elements this module hides are, by definition,
 *    below the fold — so the hiding is never seen and no CSS pre-hide is needed to
 *    stop a flash of unstyled content.
 * 2. Heroes, which animate on load from CSS instead (see base/_reveal.scss). A hero
 *    is above the fold, so a footer script hiding it would paint it solid and then
 *    snap it away.
 */
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);
/** Bands that reveal on scroll. `.block` covers all of the ACF blocks at once. */
const REVEAL_SELECTOR = [
    '.block',
    '.blog-listing',
    '.single-post-article',
    '.single-post-featured',
    '.shop-listing',
    '.error-404__inner',
    '.search-page__header',
    '.search-page__results',
    '[data-reveal]',
].join(',');
/** Heroes are handled by the CSS load-in, so they never get a scroll trigger. */
const HERO_SELECTOR = '.block.hero, .block.page-hero, .single-post-hero, .product-hero, .category-hero';
const OPT_OUT_SELECTOR = '.no-reveal, [data-reveal="off"]';
/** Travel in px. Mirrored by the hero keyframe in base/_reveal.scss — keep in step. */
const DISTANCE = 48;
const DURATION = 0.6;
const EASE = 'power2.out';
/**
 * Fires once the band's top passes this far down the viewport. Kept well clear of
 * the bottom edge: trigger too close to it and the fade has finished before the
 * band is properly on screen, so the movement is never actually seen.
 */
const START_RATIO = 0.75;
export default class Reveal {
    constructor() {
        // Honour the OS-level setting: leave every band visible and do nothing.
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return;
        }
        this.collect().forEach((element) => this.prepare(element));
        // Images and webfonts settling after init shift the triggers; recalculate
        // once everything has actually loaded.
        window.addEventListener('load', () => ScrollTrigger.refresh());
    }
    collect() {
        const nodes = Array.from(document.querySelectorAll(REVEAL_SELECTOR));
        const candidates = new Set(nodes);
        return nodes.filter((element) => {
            if (element.matches(HERO_SELECTOR)) {
                return false;
            }
            if (element.closest(OPT_OUT_SELECTOR)) {
                return false;
            }
            // A band nested inside another band would otherwise animate twice, the
            // outer one carrying the inner one along with it.
            if (this.hasAncestorIn(element, candidates)) {
                return false;
            }
            return !this.isOnScreen(element);
        });
    }
    hasAncestorIn(element, candidates) {
        let parent = element.parentElement;
        while (parent) {
            if (candidates.has(parent)) {
                return true;
            }
            parent = parent.parentElement;
        }
        return false;
    }
    /**
     * Measured against the same line the trigger uses, so a band peeking in at the
     * very bottom of the fold is not hidden only to reveal itself a pixel later.
     */
    isOnScreen(element) {
        return element.getBoundingClientRect().top < window.innerHeight * START_RATIO;
    }
    prepare(element) {
        gsap.set(element, { opacity: 0, y: DISTANCE });
        ScrollTrigger.create({
            trigger: element,
            start: `top ${START_RATIO * 100}%`,
            once: true,
            onEnter: () => {
                gsap.to(element, {
                    opacity: 1,
                    y: 0,
                    duration: DURATION,
                    ease: EASE,
                    // Drop the inline transform once it lands. A lingering transform
                    // makes the band a containing block for any position:fixed
                    // descendant, which is a trap to leave lying around.
                    clearProps: 'transform',
                });
            },
        });
    }
}
